import type { HttpClient } from '../../core/http';
import type { GetPublicIpOptions, PublicIpInfo, PublicIpService } from './types';

/**
 * Public-IP lookup helper. Wraps third-party IP-echo services behind a
 * single normalised response shape. Lives on the top-level `ProxyScrape`
 * client (as `.getPublicIp()`) because it doesn't fit cleanly inside any
 * one product module — it's an environmental fact about the caller, not
 * a free-list or checker or residential concern.
 *
 * Two providers are baked in:
 *   - ipinfo.io  → IP + geo + ASN, no key needed up to 50k req/month
 *   - api.ipify  → IP only, unlimited, no rate limits worth thinking about
 *
 * Anyone hitting our SDK at production volume should either pass an
 * `ipinfoToken` (their own ipinfo account) or override `endpoint` with
 * their own infrastructure. We don't proxy these calls through ProxyScrape
 * — they go directly from the caller to the chosen provider.
 *
 * The HttpClient is used with `skipAuth: true` so the caller's ProxyScrape
 * API key never leaves proxyscrape.com.
 */
export class PublicIpClient {
  constructor(private readonly http: HttpClient) {}

  async get(opts: GetPublicIpOptions = {}): Promise<PublicIpInfo> {
    const service: PublicIpService = opts.service ?? 'ipinfo';
    const endpoint = opts.endpoint ?? defaultEndpoint(service, opts.ipinfoToken);

    const raw = await this.http.getJson<Record<string, unknown>>(endpoint, undefined, {
      skipAuth: true,
    });

    return normalize(service, raw);
  }
}

// --- helpers ---------------------------------------------------------------

function defaultEndpoint(service: PublicIpService, ipinfoToken: string | undefined): string {
  switch (service) {
    case 'ipinfo':
      return ipinfoToken
        ? `https://ipinfo.io/json?token=${encodeURIComponent(ipinfoToken)}`
        : 'https://ipinfo.io/json';
    case 'ipify':
      return 'https://api.ipify.org?format=json';
  }
}

/**
 * Provider responses differ in field names but overlap heavily. We map to
 * a single normalised shape and keep `raw` around for callers who need
 * something we didn't lift.
 */
function normalize(service: PublicIpService, raw: Record<string, unknown>): PublicIpInfo {
  const out: PublicIpInfo = { ip: String(raw['ip'] ?? ''), raw };

  if (typeof raw['country'] === 'string') out.country = raw['country'];
  if (typeof raw['region'] === 'string') out.region = raw['region'];
  if (typeof raw['city'] === 'string') out.city = raw['city'];
  if (typeof raw['org'] === 'string') out.org = raw['org'];
  if (typeof raw['postal'] === 'string') out.postal = raw['postal'];
  if (typeof raw['timezone'] === 'string') out.timezone = raw['timezone'];

  // ipinfo returns "lat,lon" as a single field; split it into numbers for
  // typical consumers, but keep the original string under `loc` for parity.
  if (service === 'ipinfo' && typeof raw['loc'] === 'string') {
    out.loc = raw['loc'];
    const [latStr, lonStr] = raw['loc'].split(',');
    const lat = latStr ? Number(latStr) : Number.NaN;
    const lon = lonStr ? Number(lonStr) : Number.NaN;
    if (Number.isFinite(lat)) out.latitude = lat;
    if (Number.isFinite(lon)) out.longitude = lon;
  }

  return out;
}
